import { useTranslation } from "@sagot/i18n";
import { Button, Checkbox, Field, Icon, Input, Sheet, SheetContent, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger, Textarea, toast } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { TypedConfirmDialog } from "../shared/typed-confirm-dialog";
import type { EvalSchedule, EvalSet, ImportOut } from "../shared/types";
import { CsvField, ImportSummary } from "./csv-field";
import { DEFAULT_GENERATE, GenerateOptions, toGenerateIn } from "./generate-options";
import { ScheduleField } from "./new-set-dialog";
import { useCases, useEvalMutations } from "./use-evals";

function CasesTab({ set, agentId, canManage }: { set: EvalSet; agentId: string; canManage: boolean }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const cases = useCases(set.id);
  const m = useEvalMutations(agentId);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [source, setSource] = useState("");
  const rows = cases.data?.pages.flatMap((p) => p.items) ?? [];
  const add = () => {
    if (!question.trim()) { toast.error(t("build.evaluations.setSheet.questionRequired")); return; }
    m.addCase.mutate({ id: set.id, body: { question: question.trim(), expected_answer: answer.trim() || null, expected_source: source.trim() || null } }, {
      onSuccess: () => { setQuestion(""); setAnswer(""); setSource(""); toast.success(t("build.evaluations.setSheet.caseAdded")); void cases.refetch(); },
      onError: (e) => { toast.error(message(e)); },
    });
  };
  return (
    <div className="space-y-4">
      {canManage ? (
        <div className="space-y-3 rounded-lg border border-line p-4">
          <Field label={t("build.evaluations.setSheet.question")}><Input value={question} maxLength={2000} onChange={(e) => { setQuestion(e.target.value); }} /></Field>
          <Field label={t("build.evaluations.setSheet.expectedAnswer")}><Textarea rows={2} maxLength={4000} value={answer} onChange={(e) => { setAnswer(e.target.value); }} /></Field>
          <Field label={t("build.evaluations.setSheet.expectedSource")}><Input value={source} maxLength={300} onChange={(e) => { setSource(e.target.value); }} /></Field>
          <Button size="sm" onClick={add} loading={m.addCase.isPending}><Icon name="plus" />{t("build.evaluations.setSheet.addCase")}</Button>
        </div>
      ) : null}
      {cases.isPending ? <Skeleton className="h-24" /> : null}
      {cases.data && rows.length === 0 ? <p className="text-sm text-fg-muted">{t("build.evaluations.setSheet.noCases")}</p> : null}
      <ul className="divide-y divide-line rounded-lg border border-line" aria-label={t("build.evaluations.setSheet.cases")}>
        {rows.map((c) => (
          <li key={c.id} className="flex items-start gap-3 px-4 py-3">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">{c.question}</div>
              {c.expected_answer ? <div className="mt-0.5 line-clamp-2 text-xs text-fg-muted">{c.expected_answer}</div> : null}
              {c.expected_source ? <div className="mt-0.5 font-mono text-[11px] text-fg-subtle">{c.expected_source}</div> : null}
            </div>
            {canManage ? (
              <Button variant="ghost" size="sm" iconOnly aria-label={t("build.evaluations.setSheet.deleteCase")} onClick={() => { m.deleteCase.mutate(c.id, { onSuccess: () => { toast.success(t("build.evaluations.setSheet.caseDeleted")); void cases.refetch(); } }); }}>
                <Icon name="trash" />
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      {cases.hasNextPage ? <Button variant="secondary" size="sm" loading={cases.isFetchingNextPage} onClick={() => void cases.fetchNextPage()}>{t("build.evaluations.setSheet.loadMore")}</Button> : null}
    </div>
  );
}

function ImportTab({ set, agentId }: { set: EvalSet; agentId: string }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const m = useEvalMutations(agentId);
  const [csv, setCsv] = useState("");
  const [check, setCheck] = useState<ImportOut | null>(null);
  const [generate, setGenerate] = useState(DEFAULT_GENERATE);
  const fail = (e: unknown) => { toast.error(message(e)); };
  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <CsvField value={csv} onChange={(next) => { setCsv(next); setCheck(null); }} />
        {check ? <ImportSummary result={check} /> : null}
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" disabled={!csv.trim()} loading={m.importCsv.isPending && m.importCsv.variables.dryRun} onClick={() => { m.importCsv.mutate({ id: set.id, csv, dryRun: true }, { onSuccess: setCheck, onError: fail }); }}>{t("build.evaluations.csv.preview")}</Button>
          <Button size="sm" disabled={!check || check.valid === 0} loading={m.importCsv.isPending && !m.importCsv.variables.dryRun} onClick={() => { m.importCsv.mutate({ id: set.id, csv, dryRun: false }, { onSuccess: (r) => { toast.success(t("build.evaluations.csv.imported", { count: r.imported })); setCsv(""); setCheck(null); }, onError: fail }); }}>
            {t("build.evaluations.csv.importButton", { count: check?.valid ?? 0 })}
          </Button>
        </div>
      </section>
      <section className="space-y-3 border-t border-line pt-6">
        <h3 className="flex items-center gap-2 text-sm font-bold"><Icon name="sparkles" className="size-4 text-primary-ink" />{t("build.evaluations.generate.title")}</h3>
        <GenerateOptions value={generate} onChange={setGenerate} />
        <Button size="sm" loading={m.generate.isPending} onClick={() => { m.generate.mutate({ id: set.id, body: toGenerateIn(generate) }, { onSuccess: (r) => { toast.success(t("build.evaluations.generate.generated", { count: r.created })); }, onError: fail }); }}>
          {t("build.evaluations.generate.submit")}
        </Button>
      </section>
    </div>
  );
}

function ScheduleTab({ set, agentId, onDelete }: { set: EvalSet; agentId: string; onDelete: () => void }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const m = useEvalMutations(agentId);
  const [schedule, setSchedule] = useState<EvalSchedule>(set.schedule as EvalSchedule);
  const [alert, setAlert] = useState(set.alert_on_drop);
  return (
    <div className="space-y-5">
      <ScheduleField value={schedule} onChange={setSchedule} />
      <Checkbox checked={alert} onCheckedChange={(v) => { setAlert(v === true); }} label={t("build.evaluations.setSheet.alert")} />
      <Button size="sm" loading={m.patchSet.isPending} onClick={() => { m.patchSet.mutate({ id: set.id, body: { schedule, alert_on_drop: alert } }, { onSuccess: () => { toast.success(t("build.evaluations.setSheet.scheduleSaved")); }, onError: (e) => { toast.error(message(e)); } }); }}>
        {t("build.evaluations.setSheet.saveSchedule")}
      </Button>
      <div className="border-t border-line pt-5">
        <Button variant="danger" size="sm" onClick={onDelete}><Icon name="trash" />{t("build.evaluations.setSheet.deleteSet")}</Button>
      </div>
    </div>
  );
}

/** Test set drawer: cases, CSV import / AI generation, schedule + alert, delete. */
export function SetSheet({ set, agentId, canManage, onClose }: { set: EvalSet; agentId: string; canManage: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const m = useEvalMutations(agentId);
  const [deleting, setDeleting] = useState(false);
  return (
    <Sheet open onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent title={set.name} description={t("build.evaluations.sets.cases", { count: set.case_count })} closeLabel={t("common.close")} className="max-w-2xl">
        <Tabs defaultValue="cases">
          <TabsList>
            <TabsTrigger value="cases">{t("build.evaluations.setSheet.cases")}</TabsTrigger>
            {canManage ? <TabsTrigger value="import">{t("build.evaluations.setSheet.import")}</TabsTrigger> : null}
            {canManage ? <TabsTrigger value="schedule">{t("build.evaluations.setSheet.schedule")}</TabsTrigger> : null}
          </TabsList>
          <TabsContent value="cases" className="pt-4"><CasesTab set={set} agentId={agentId} canManage={canManage} /></TabsContent>
          {canManage ? <TabsContent value="import" className="pt-4"><ImportTab set={set} agentId={agentId} /></TabsContent> : null}
          {canManage ? <TabsContent value="schedule" className="pt-4"><ScheduleTab set={set} agentId={agentId} onDelete={() => { setDeleting(true); }} /></TabsContent> : null}
        </Tabs>
        <TypedConfirmDialog
          open={deleting}
          onOpenChange={setDeleting}
          title={t("build.evaluations.setSheet.deleteTitle", { name: set.name })}
          body={t("build.evaluations.setSheet.deleteBody")}
          confirmLabel={t("build.evaluations.setSheet.deleteConfirm")}
          expected={set.name}
          submitLabel={t("build.evaluations.setSheet.deleteSet")}
          pending={m.deleteSet.isPending}
          error={m.deleteSet.isError ? message(m.deleteSet.error) : null}
          onConfirm={() => { m.deleteSet.mutate(set.id, { onSuccess: () => { toast.success(t("build.evaluations.setSheet.deleted")); onClose(); } }); }}
        />
      </SheetContent>
    </Sheet>
  );
}
